import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

class EmeVideo extends Component {
  requiredRefs = ['video'];
  #observer = null;
  #intersecting = false;
  #userPaused = false;

  connectedCallback() {
    super.connectedCallback();
    const { video } = this.refs;
    for (const type of ['play', 'pause', 'ended']) video.addEventListener(type, this.#syncPlayback);
    this.#syncPlayback();
    this.#observer = new IntersectionObserver(this.#handleIntersect, { rootMargin: '200px 0px', threshold: [0, 0.01] });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { video } = this.refs;
    if (video) for (const type of ['play', 'pause', 'ended']) video.removeEventListener(type, this.#syncPlayback);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  get #autoplay() {
    return this.dataset.autoplay !== 'false';
  }

  toggleSound() {
    this.setMuted(!this.refs.video.muted);
  }

  setMuted(muted) {
    this.refs.video.muted = muted;
    this.refs.soundToggle?.setAttribute('aria-pressed', String(!muted));
  }

  togglePlayback() {
    this.setPaused(!this.refs.video.paused);
  }

  setPaused(paused) {
    this.#userPaused = paused;
    if (paused) this.refs.video.pause();
    else this.#play({ force: true });
  }

  reset() {
    this.setPaused(true);
    if (this.refs.video.getAttribute('src')) this.refs.video.currentTime = 0;
  }

  release() {
    this.#userPaused = false;
    if (this.#intersecting && this.#autoplay) this.#play();
  }

  #syncPlayback = () => {
    const { video, playbackToggle } = this.refs;
    const paused = video.paused;
    this.toggleAttribute('data-paused', paused);
    if (playbackToggle instanceof HTMLElement) {
      playbackToggle.setAttribute('aria-label', playbackToggle.dataset[paused ? 'labelPlay' : 'labelPause'] ?? '');
    }
  };

  #handleIntersect = (entries) => {
    const entry = entries.at(-1);
    if (!entry) return;
    const visible = entry.isIntersecting && entry.intersectionRatio > 0;
    this.#intersecting = visible;
    if (entry.isIntersecting) this.#applyPoster();
    if (!visible) this.refs.video.pause();
    else if (this.#autoplay && !this.#userPaused) this.#play();
  };

  #applyPoster() {
    const { video } = this.refs;
    const { poster } = video.dataset;
    if (!poster || video.getAttribute('poster')) return;
    video.poster = poster;
  }

  #load() {
    this.#applyPoster();
    const { video } = this.refs;
    const { src } = video.dataset;
    if (!src || video.getAttribute('src')) return;
    if (!video.poster) video.preload = 'metadata';
    video.src = src;
  }

  async #play({ force = false } = {}) {
    this.#load();
    if (prefersReducedMotion() && !force) return;
    try {
      await this.refs.video.play();
    } catch {
      return;
    }
  }
}

if (!customElements.get('eme-video')) customElements.define('eme-video', EmeVideo);
